import React, { useState, useEffect } from 'react';
import { ReactComponent as IconAudio } from "../../images/svg/audio.svg";
import { ReactComponent as IconVideo } from "../../images/svg/video.svg";
import { ReactComponent as IconVirtualBackgrounds } from "../../images/svg/virtual-background.svg";
import { ReactComponent as IconNotifications } from "../../images/svg/bell.svg";
import { ReactComponent as IconProfile } from "../../images/svg/user.svg";
import { ReactComponent as IconShortcuts } from "../../images/svg/shortcuts.svg";
import { ReactComponent as IconClose } from "../../images/svg/close.svg";
import { ReactComponent as IconArrowUp } from "../../images/svg/arrow-up.svg";

const SettingsDialog = ({ isOpen, onClose, localUsername, onUpdateUsername, getAvatarColor }) => {
    const [activeTab, setActiveTab] = useState('notifications');
    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [selectedAudioInput, setSelectedAudioInput] = useState('');
    const [selectedAudioOutput, setSelectedAudioOutput] = useState('');
    const [editedUsername, setEditedUsername] = useState(localUsername);
    const [error, setError] = useState('');
    const [noiseSuppression, setNoiseSuppression] = useState(false);
    const [notifications, setNotifications] = useState({
        chatMessages: {
            label: "Chat messages",
            enabled: localStorage.getItem('chatMessagesEnabled') === 'true'
        }
    });
    const [enableShortcuts, setEnableShortcuts] = useState(true);

    const shortcuts = [
        { action: "Show or hide video thumbnails", key: "F" },
        { action: "Mute or unmute your microphone", key: "M" },
        { action: "Start or stop your camera", key: "V" },
        { action: "Open or close the chat", key: "C" },
        { action: "Switch between camera or screen sharing", key: "D" },
        { action: "Show or hide the participants panel", key: "P" },
        { action: "Raise or lower your hand", key: "R" },
        { action: "View or exit full screen", key: "S" },
        { action: "Toggle tile view", key: "W" },
        { action: "Show participants stats", key: "T" },
        { action: "Send thumbs up reaction", key: "Alt + T" }
    ];

    useEffect(() => {
        setEditedUsername(localUsername);
        setError('');
    }, [localUsername, isOpen]);

    useEffect(() => {
        const getAudioDevices = async () => {
            try {
                // Request permission to access media devices
                await navigator.mediaDevices.getUserMedia({ audio: true });
                
                // Get list of devices
                const devices = await navigator.mediaDevices.enumerateDevices();
                
                const inputs = devices.filter(device => device.kind === 'audioinput');
                const outputs = devices.filter(device => device.kind === 'audiooutput');
                
                setAudioInputDevices(inputs);
                setAudioOutputDevices(outputs);
                
                // Set default devices if available
                if (inputs.length > 0) setSelectedAudioInput(inputs[0].deviceId);
                if (outputs.length > 0) setSelectedAudioOutput(outputs[0].deviceId);
            } catch (error) {
                console.error('Error accessing media devices:', error);
            }
        };

        if (isOpen) {
            getAudioDevices();
        }
    }, [isOpen]);

    const handleNotificationChange = (key) => {
        const newValue = !notifications[key].enabled;
        setNotifications(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                enabled: newValue
            }
        }));
        localStorage.setItem('chatMessagesEnabled', newValue);
    };

    const getInitials = (name) => {
        if (!name) return '';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // const getAvatarColor = (name) => {
    //     if (!name) return '#7B7BFF';
    //     let hash = 0;
    //     for (let i = 0; i < name.length; i++) {
    //         hash = 31 * hash + name.charCodeAt(i);
    //     }
    //     const colors = ['#FF7B7B', '#7B7BFF', '#7BFF7B', '#FFFF7B', '#FF7BFF', '#7BFFFF'];
    //     const index = Math.abs(hash % colors.length);
    //     return colors[index];
    // };

    const handleUsernameChange = (e) => {
        const newUsername = e.target.value;
        setEditedUsername(newUsername);
        
        if (newUsername.trim() === '') {
            setError('Username cannot be empty');
        } else if (newUsername.length > 50) {
            setError('Username cannot exceed 50 characters');
        } else {
            setError('');
        }
    };

    const handleOkClick = () => {
        if (!error && editedUsername.trim() !== '') {
            // Update username in localStorage
            const roomName = window.location.pathname.split('/')[1];
            const roomCredentials = JSON.parse(localStorage.getItem(`room-${roomName}`)) || {};
            roomCredentials.username = editedUsername;
            localStorage.setItem(`room-${roomName}`, JSON.stringify(roomCredentials));
            
            // Call the callback function to update username in parent component
            onUpdateUsername(editedUsername);
            onClose();
        }
    };

    const sidebarItems = [
        { id: 'audio', label: 'Audio', icon: IconAudio, enabled: false },
        { id: 'video', label: 'Video', icon: IconVideo, enabled: false },
        { id: 'virtual-backgrounds', label: 'Virtual Backgrounds', icon: IconVirtualBackgrounds, enabled: false },
        { id: 'notifications', label: 'Notifications', icon: IconNotifications, enabled: true },
        { id: 'profile', label: 'Profile', icon: IconProfile, enabled: false },
        { id: 'shortcuts', label: 'Shortcuts', icon: IconShortcuts, enabled: false }
    ];

    if (!isOpen) return null;

    return (
        <div className="settings-dialog-overlay" onClick={e => e.stopPropagation()}>
            <div className="settings-dialog">
                    <button className="close-button" onClick={onClose}>
                        <IconClose />
                    </button>
                <div className="settings-content">
                    <div className="settings-sidebar">
                        {sidebarItems.map(item => (
                            <div 
                                key={item.id}
                                className={`sidebar-item ${activeTab === item.id ? 'active' : ''} ${!item.enabled ? 'disabled' : ''}`}
                                onClick={() => item.enabled && setActiveTab(item.id)}
                            >
                                <span className="sidebar-icon">
                                    <item.icon />
                                </span>
                                {item.label}
                            </div>
                        ))}
                    </div>
                    <div className="settings-main-content">
                        {activeTab === 'audio' && (
                            <div className="audio-settings">
                                <div className="setting-group">
                                    <label>Microphone</label>
                                    <div className="select-wrapper">
                                        <select 
                                            className="settings-select"
                                            value={selectedAudioInput}
                                            onChange={(e) => setSelectedAudioInput(e.target.value)}
                                        >
                                            {audioInputDevices.map(device => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || `Microphone ${device.deviceId}`}
                                                </option>
                                            ))}
                                        </select>
                                        <IconArrowUp className="select-arrow" />
                                    </div>
                                </div>
                                <div className="setting-group">
                                    <label>Audio Output</label>
                                    <div className="audio-output-container">
                                        <div className="select-wrapper">
                                            <select 
                                                className="settings-select"
                                                value={selectedAudioOutput}
                                                onChange={(e) => setSelectedAudioOutput(e.target.value)}
                                            >
                                                {audioOutputDevices.map(device => (
                                                    <option key={device.deviceId} value={device.deviceId}>
                                                        {device.label || `Speaker ${device.deviceId}`}
                                                    </option>
                                                ))}
                                            </select>
                                            <IconArrowUp className="select-arrow" />
                                        </div>
                                        <button className="test-button">Test</button>
                                    </div>
                                </div>
                                <div className="setting-group">
                                    <label>Noise Suppression</label>
                                    <div 
                                        className={`notification-item ${noiseSuppression ? 'active' : ''}`}
                                        onClick={() => setNoiseSuppression(!noiseSuppression)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span>Activate Suppression</span>
                                        <div className="checkbox-wrapper" onClick={e => e.stopPropagation()}>
                                            <input 
                                                type="checkbox"
                                                checked={noiseSuppression}
                                                onChange={(e) => setNoiseSuppression(e.target.checked)}
                                            />
                                            <span className="checkmark"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'notifications' && (
                            <div className="notifications-settings">
                                <h3>Notification Settings</h3>
                                <div className="setting-group">
                                    {Object.entries(notifications).map(([key, value]) => (
                                        <div 
                                            key={key}
                                            className={`notification-item ${value.enabled ? 'active' : ''}`}
                                            onClick={() => handleNotificationChange(key)}
                                        >
                                            <span>{value.label}</span>
                                            <div className="checkbox-wrapper">
                                                <input 
                                                    type="checkbox"
                                                    checked={value.enabled}
                                                    onChange={() => handleNotificationChange(key)}
                                                />
                                                <span className="checkmark"></span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {activeTab === 'profile' && (
                            <div className="profile-settings">
                                <div className="profile-header">
                                    <div 
                                        className="profile-avatar"
                                        style={{ 
                                            backgroundColor: getAvatarColor(localUsername),
                                        }}
                                    >
                                        {getInitials(editedUsername)}
                                    </div>
                                    <h2>{editedUsername}</h2>
                                </div>
                                <div className="setting-group">
                                    <label>Name</label>
                                    <div className="input-group">
                                        <input 
                                            type="text" 
                                            className={`settings-input ${error ? 'error' : ''}`}
                                            value={editedUsername || ''}
                                            onChange={handleUsernameChange}
                                            placeholder="Enter your name"
                                        />
                                        {error && <div className="error-message-settings">{error}</div>}
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'shortcuts' && (
                            <div className="shortcuts-settings">
                                <div className="shortcuts-header">
                                    <label className="checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            checked={enableShortcuts}
                                            onChange={(e) => setEnableShortcuts(e.target.checked)}
                                        />
                                        <span className="checkmark"></span>
                                        <span>Enable Keyboard shortcuts</span>
                                    </label>
                                </div>
                                <div className="shortcuts-list">
                                    {shortcuts.map((shortcut, index) => (
                                        <div key={index} className="shortcut-item">
                                            <span className="shortcut-action">{shortcut.action}</span>
                                            <span className="shortcut-key">{shortcut.key}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}


                    <div className="settings-footer">
                        <button className="cancel-button" onClick={onClose}>Cancel</button>
                        <button 
                            className="ok-button" 
                            onClick={handleOkClick}
                            disabled={!!error || editedUsername.trim() === ''}
                        >
                            Ok
                        </button>
                    </div>
                    </div>
                </div>
                
            </div>
        </div>
    );
};

export default SettingsDialog; 